import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  Plus, Search, Edit2, Trash2, X,
  LayoutDashboard, UtensilsCrossed, Menu as MenuIcon,
  ReceiptText, FileBarChart, Settings as SettingsIcon, LogOut
} from "lucide-react";
import "./Menu.css";
import { ChevronRight, ChevronLeft } from "lucide-react";
import logo from "../assets/lg.png"; // ✅ ADD THIS AT TOP

function Menu() {
  const navigate = useNavigate();
  const API_URL = `${API_BASE_URL}/menu.php`;
  
  const [menuItems, setMenuItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newItem, setNewItem] = useState({ name: "", category: "Starter", price: "" });
 const [isSidebarOpen, setIsSidebarOpen] = useState(false);
 const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Load Menu from Database
  const fetchMenu = async () => {
    try {
      const res = await axios.get(API_URL);
      setMenuItems(res.data);
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      const itemData = editingItem ? { ...newItem, id: editingItem.id } : newItem;
      const res = await axios.post(API_URL, itemData);
      if (res.data.status === "success") {
        fetchMenu();
        closeModal();
      }
    } catch (err) {
      alert("Failed to save item. Check connection.");
    }
  };

  const deleteItem = async (id) => {
    if (window.confirm("Delete this item permanently?")) {
      try {
        const res = await axios.delete(`${API_URL}?id=${id}`);
        if (res.data.status === "success") fetchMenu();
      } catch (err) {
        alert("Error deleting item.");
      }
    }
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setNewItem({ name: item.name, category: item.category, price: item.price });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setNewItem({ name: "", category: "Starter", price: "" });
  };

  const filteredItems = menuItems.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="dash-container">
      {/* SIDEBAR - Updated with dynamic class */}
     <aside className={`sidebar ${isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>

  {/* COLLAPSE BUTTON */}
  <button 
    className="sidebar-toggle-btn d-none d-md-flex"
    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
  >
    {isSidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
  </button>

  <div className="sidebar-top">
    
    {/* ✅ LOGO SECTION (same as Dashboard) */}
    <div className="brand">
      <img src={logo} alt="Logo" className="sidebar-logo" />

      {/* Mobile Close */}
      <X 
        className="d-md-none ms-auto cursor-pointer" 
        onClick={() => setIsSidebarOpen(false)} 
      />
    </div>

    {/* NAVIGATION */}
    <nav className="nav-menu">
      <div className="nav-item" onClick={() => navigate("/dashboard")}>
        <LayoutDashboard size={20} /> {!isSidebarCollapsed && "Dashboard"}
      </div>

      <div className="nav-item" onClick={() => navigate("/tables")}>
        <UtensilsCrossed size={20} /> {!isSidebarCollapsed && "Tables"}
      </div>

      <div className="nav-item active" onClick={() => navigate("/menu")}>
        <MenuIcon size={20} /> {!isSidebarCollapsed && "Menu"}
      </div>

      <div className="nav-item" onClick={() => navigate("/billing")}>
        <ReceiptText size={20} /> {!isSidebarCollapsed && "Billing"}
      </div>

      <div className="nav-item" onClick={() => navigate("/reports")}>
        <FileBarChart size={20} /> {!isSidebarCollapsed && "Reports"}
      </div>
    </nav>
  </div>

  {/* BOTTOM */}
  <div className="sidebar-bottom">
    <div className="nav-item" onClick={() => navigate("/settings")}>
      <SettingsIcon size={20} /> {!isSidebarCollapsed && "Settings"}
    </div>

    <div className="nav-item logout-red" onClick={() => navigate("/")}>
      <LogOut size={20} /> {!isSidebarCollapsed && "Logout"}
    </div>
  </div>

</aside>

      {/* MAIN CONTENT */}
      <main className="main-content">
        {/* Added mobile-top-bar */}
        <div className="mobile-top-bar d-md-none">
          <div className="brand-text">Dine Spark</div>
          <MenuIcon className="cursor-pointer" onClick={() => setIsSidebarOpen(true)} />
        </div>

        <header className="page-header">
          <div className="header-titles">
            <h1 className="serif-title">Menu Management</h1>
            <p className="subtitle">Add, edit, and manage your menu</p>
          </div>
          <button className="btn-add-menu" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Add Menu Item
          </button>
        </header>

        <div className="search-section">
          <div className="search-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search database items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* 1. DESKTOP VIEW: Table (Hidden on mobile) */}
        <div className="menu-table-container d-none d-md-block">
          <table className="menu-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Category</th>
                <th>Price</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium">{item.name}</td>
                  <td><span className="badge-category">{item.category}</span></td>
                  <td>₹{item.price}</td>
                  <td className="text-right actions-cell">
                    <button className="icon-btn edit" onClick={() => handleEditClick(item)}><Edit2 size={16} /></button>
                    <button className="icon-btn delete" onClick={() => deleteItem(item.id)}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 2. MOBILE VIEW: Cards (Hidden on desktop - Matches your photo) */}
        <div className="mobile-card-list d-md-none">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div className="menu-card-ui" key={item.id}>
                <div className="card-info">
                  <div className="name-row">
                    <span className="item-name">{item.name}</span>
                    <span className="item-category-badge">{item.category}</span>
                  </div>
                  <div className="item-price">₹{parseFloat(item.price).toFixed(2)}</div>
                </div>
                <div className="card-actions">
                  <Edit2 size={18} className="edit-icon-btn" onClick={() => handleEditClick(item)} />
                  <Trash2 size={18} className="delete-icon-btn" onClick={() => deleteItem(item.id)} />
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-muted mt-4">No items found.</p>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingItem ? "Edit Item" : "Add Item"}</h2>
                <X className="close-btn" onClick={closeModal} size={20} />
              </div>
              <form onSubmit={handleSaveItem}>
                <div className="form-group">
                  <label>Item Name</label>
                  <input required type="text" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Price (₹)</label>
                  <input required type="number" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}>
                    <option>Starter</option>
                    <option>Main Course</option>
                    <option>Dessert</option>
                    <option>Beverage</option>
                  </select>
                </div>
                <button type="submit" className="modal-submit-btn">Save to Database</button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Menu;